import React, { useEffect, useMemo, useRef, useState } from "react";
import { normalizeCompany, suggestCompany } from "./schema";

// Komorka "Nazwa firmy" - autocomplete z podpowiedziami ze slownika
// Magazyn Warszawa. Uzytkownik wpisuje tekst, dostaje liste dopasowan,
// podpowiedz "czy chodzilo o..." oraz mozliwosc dodania nowej firmy do bazy.
//
// props:
//   value          - biezaca wartosc komorki
//   dataCell       - "<rowIdx>:<colKey>" (do nawigacji fokusem w tabeli)
//   companies      - string[] nazw firm ze slownika
//   dict           - Map(znormalizowany klucz -> pisownia kanoniczna)
//   hasError       - czy pokazac stan bledu
//   errorMessage   - tresc bledu (tooltip)
//   onChange(v)    - zmiana tekstu w polu
//   onCommit(v)    - zatwierdzenie wartosci (wybor z listy / dodanie)
//   onBlurCommit() - oznacz komorke jako dotknieta + auto-snap
//   onKeyDown(e)   - fallback nawigacji z tabeli (Enter -> nastepny wiersz)
//   onAddCompany(name) => Promise<string>  - dodaje firme do bazy, zwraca kanoniczna nazwe

const MAX_MATCHES = 8;

const wrapStyle = { position: "relative", width: "100%" };

const menuStyle = {
  position: "fixed",
  zIndex: 2000,
  background: "#fff",
  border: "1px solid #bbb",
  borderRadius: 4,
  boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
  maxHeight: 320,
  overflowY: "auto",
  fontSize: 15,
};

const itemStyle = {
  padding: "9px 12px",
  cursor: "pointer",
  borderBottom: "1px solid #f0f0f0",
  whiteSpace: "nowrap",
};

const actionBtn = {
  border: "1px solid #1976d2",
  color: "#1976d2",
  background: "#fff",
  borderRadius: 4,
  padding: "3px 10px",
  cursor: "pointer",
  fontSize: 13,
};

const CompanyCell = ({
  value,
  dataCell,
  companies = [],
  dict,
  hasError,
  errorMessage,
  onChange,
  onCommit,
  onBlurCommit,
  onKeyDown,
  onAddCompany,
}) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [hi, setHi] = useState(-1);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  const q = normalizeCompany(value);

  const matches = useMemo(() => {
    if (!q) return [];
    const out = [];
    for (const c of companies) {
      if (normalizeCompany(c).includes(q)) {
        out.push(c);
        if (out.length >= MAX_MATCHES) break;
      }
    }
    return out;
  }, [q, companies]);

  const exact = Boolean(q) && dict && dict.has(q);
  const suggestion = useMemo(
    () => (!exact && q ? suggestCompany(value, dict) : null),
    [exact, q, value, dict],
  );
  const showSuggestion =
    suggestion && !matches.some((m) => normalizeCompany(m) === normalizeCompany(suggestion));
  const canAdd = Boolean(q) && !exact && typeof onAddCompany === "function";

  const updateRect = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    // zamknij menu przy scrollu (fixed menu nie podaza za tabela)
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const openMenu = () => {
    updateRect();
    setHi(-1);
    setAddError("");
    setOpen(true);
  };

  const selectValue = (v) => {
    onCommit(v);
    setOpen(false);
    setHi(-1);
  };

  const handleAdd = async () => {
    const name = (value || "").trim();
    if (!name) return;
    // jesli po normalizacji juz jest w slowniku - tylko zatwierdz
    if (dict && dict.has(normalizeCompany(name))) {
      selectValue(dict.get(normalizeCompany(name)));
      return;
    }
    setAdding(true);
    setAddError("");
    try {
      const canonical = await onAddCompany(name);
      selectValue(canonical || name.toUpperCase());
    } catch (e) {
      setAddError("Nie udało się dodać firmy do bazy");
    } finally {
      setAdding(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) openMenu();
      setHi((h) => Math.min(h + 1, matches.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, -1));
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      if (open && hi >= 0 && matches[hi]) {
        e.preventDefault();
        selectValue(matches[hi]);
        return;
      }
      if (open && exact) {
        // zatwierdz kanoniczna pisownie, ale pozwol tabeli przejsc do nastepnego wiersza
        selectValue(dict.get(q));
      }
    }
    if (typeof onKeyDown === "function") onKeyDown(e);
  };

  const inputStyle = {
    border: `1px solid ${hasError ? "#d32f2f" : "#ccc"}`,
    borderRadius: 4,
    padding: "10px 12px",
    font: "inherit",
    fontSize: 16,
    width: "100%",
    boxSizing: "border-box",
    background: hasError ? "#fff5f5" : "#fff",
  };

  return (
    <div style={wrapStyle}>
      <input
        ref={inputRef}
        type="text"
        autoComplete="off"
        data-cell={dataCell}
        value={value ?? ""}
        title={hasError ? errorMessage : undefined}
        style={inputStyle}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) openMenu();
          else updateRect();
          setHi(-1);
        }}
        onFocus={openMenu}
        onBlur={() => {
          setOpen(false);
          if (typeof onBlurCommit === "function") onBlurCommit();
        }}
        onKeyDown={handleKeyDown}
      />

      {open && rect && (
        <div
          style={{
            ...menuStyle,
            top: rect.bottom + 2,
            left: rect.left,
            width: Math.max(rect.width, 260),
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {matches.map((c, i) => (
            <div
              key={`${c}-${i}`}
              style={{
                ...itemStyle,
                background: i === hi ? "#e3f2fd" : "#fff",
              }}
              onMouseEnter={() => setHi(i)}
              onClick={() => selectValue(c)}
            >
              {c}
            </div>
          ))}

          {!q && (
            <div style={{ ...itemStyle, color: "#888", cursor: "default" }}>
              Zacznij pisać nazwę firmy…
            </div>
          )}

          {q && !exact && matches.length === 0 && (
            <div style={{ ...itemStyle, color: "#888", cursor: "default" }}>
              Brak dopasowań w słowniku
            </div>
          )}

          {exact && (
            <div
              style={{ ...itemStyle, color: "#2e7d32", cursor: "default" }}
            >
              ✓ Firma jest w słowniku
            </div>
          )}

          {(showSuggestion || canAdd) && (
            <div
              style={{
                padding: "8px 10px",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
                background: "#fafafa",
              }}
            >
              {showSuggestion && (
                <>
                  <span style={{ color: "#555" }}>
                    Czy chodziło o:{" "}
                    <strong>{suggestion}</strong>?
                  </span>
                  <button
                    type="button"
                    style={actionBtn}
                    onClick={() => selectValue(suggestion)}
                  >
                    Użyj
                  </button>
                </>
              )}
              {canAdd && (
                <button
                  type="button"
                  style={{ ...actionBtn, borderColor: "#2e7d32", color: "#2e7d32" }}
                  disabled={adding}
                  onClick={handleAdd}
                >
                  {adding
                    ? "Dodawanie…"
                    : `+ Dodaj „${(value || "").trim()}” do bazy`}
                </button>
              )}
              {addError && (
                <span style={{ color: "#d32f2f", width: "100%" }}>
                  {addError}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyCell;
